import { Box, Chip } from '@mui/material';
import { DocumentType } from '@synergy/models';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router';
import {
    PaloConfirmationDialog,
    PaloDetailView,
    PaloHistory,
    PaloRecordMenu,
    PaloStep,
    PaloTabs,
} from '@synergy/components-palo';
import { PoolDocumentTitle } from '@synergy/components-next';
import { PoolFormattedMessage } from '@synergy/components';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { SubPageHandle } from '@synergy/utils';

import * as ActivityActions from '../../../../common/actions/activity-actions';
import * as CustomerConditionActions from '../customer-detail/conditions/store/customer-conditions-actions';
import * as CustomerDetailActions from '../customer-detail/store/customer-detail-actions';
import * as CustomerDetailOverviewActions from '../customer-detail/overview/store/customer-detail-overview-actions';
import { CustomerDetailAddresses } from '../customer-detail/addresses/customer-detail-addresses';
import { CustomerDetailConditions } from '../customer-detail/conditions/customer-detail-conditions';
import { CustomerDetailDocuments } from '../customer-detail/documents/customer-detail-documents';
import { CustomerDetailExternalCustomerMapping } from '../customer-detail/external-customer-mapping/customer-detail-external-customer-mapping';
import { CustomerDetailOverview } from '../customer-detail/overview/customer-detail-overview';
import { CustomerDetailTabs } from '../customer-detail/models/enum/customer-detail-tabs';
import { CustomerEmployees } from '../customer-detail/employees/customer-employees';
import { CustomerTopics } from '../customer-detail/topics/customer-topics';
import { DetailStream } from '../../../activity-sidebar/details/detail-stream';
import { NoteType } from '../../../../common/models/enums/note-type';
import { PaloAccountTransactions } from '../../../../common/components/palo/palo-account-transactions';
import { PaloCustomerDocuments } from './palo-customer-documents';
import {
    PaloDirtyDot,
    PaloDirtyProvider,
    PaloDirtyScope,
    usePaloSaveOnLeave,
} from '../../../../common/components/palo/palo-dirty-context';
import { PaloRecordDock } from '../../../../common/components/palo/palo-record-dock';
import { PaloRecordNotices } from '../../../../common/components/palo/palo-record-notices';
import { PaloSaveAction, paloDocumentPhase } from '../../../detail-header/palo-main-action';
import { ROUTES } from '../../../sidebar-menu/navigation-drawer/routes';
import { customerConditionSelector } from '../customer-detail/conditions/store/customer-conditions-selectors';
import { customerDetailConditionsValidationSchema } from '../customer-detail/conditions/customer-detail-conditions-validation-schema';
import { customerDetailMemberSelector } from '../customer-detail/store/customer-detail-selectors';
import {
    customerDetailOverviewFormDataSelector,
    customerDetailOverviewSelector,
} from '../customer-detail/overview/store/customer-detail-overview-selectors';
import { documentsSelector as customerDocumentsSelector } from '../customer-detail/documents/store/customer-detail-documents-selectors';
import { paloSchemaErrors } from '../../../../common/components/palo/palo-schema-errors';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { usePaloRecordKey } from '../../components/use-palo-record-key';
import { useRedirectAfterNewSave } from '../../components/use-redirect-after-new-save';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const PaloCustomerDetailInner = (): ReactElement => {
    const subPageRef = useRef<SubPageHandle>(null);
    const { id: customerIdParam } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const intl = useIntl();
    const dispatch = useThunkDispatch();

    const { customer, baseline, isValid: overviewIsValid } = useAppSelector(customerDetailOverviewSelector);
    const addressCount = useAppSelector(customerDetailOverviewFormDataSelector).formValues.addresses?.length ?? 0;
    const documentCount = useAppSelector(customerDocumentsSelector).length;
    const conditionsState = useAppSelector(customerConditionSelector);
    const { isMember, memberSince } = useAppSelector(customerDetailMemberSelector);
    const isNew = customerIdParam ? isNaN(+customerIdParam) : false;
    const customerName = isNew
        ? intl.formatMessage({ id: 'app.contactsAndOrganizations.customer.new' })
        : customer?.name ?? '';
    const isLocked = customer?.isLocked ?? false;

    const [showDeleteWarning, setShowDeleteWarning] = useState(false);
    const [showLockWarning, setShowLockWarning] = useState(false);
    const [showUnlockWarning, setShowUnlockWarning] = useState(false);

    useRedirectAfterNewSave(ROUTES.contactsAndOrganizations.customers.detail, customer?.id);
    usePaloSaveOnLeave(subPageRef.current?.submit);

    useEffect(() => {
        dispatch(ActivityActions.setDocumentType(DocumentType.Customer));

        return () => {
            dispatch(CustomerDetailActions.resetCustomerDetails());
            dispatch(ActivityActions.resetDocumentType());
            dispatch(ActivityActions.resetActivities());
        };
    }, [dispatch]);

    useEffect(() => {
        if (customer?.id) {
            dispatch(ActivityActions.fetchActivities(DocumentType.Customer, customer.id));
        }
    }, [customer, dispatch]);

    useEffect(() => {
        // Whenever the store holds another record than the path names (#2326 R-243).
        if (customerIdParam && !isNaN(+customerIdParam) && customer?.id !== +customerIdParam) {
            dispatch(CustomerDetailOverviewActions.fetchCustomerDetails(+customerIdParam));
        }
    }, [customer?.id, customerIdParam, dispatch]);

    // Fetched with the record, not with its tab: the save refuses over required
    // fields on the conditions tab, and until they are here it has nothing to
    // refuse over (#2212, R-28). Once per record — conditions left in the store
    // belong to the record before (#2326 R-243).
    const conditionsFor = useRef<number | undefined>(undefined);
    useEffect(() => {
        if (customer?.id && (customer.id !== conditionsFor.current || !conditionsState.conditions)) {
            conditionsFor.current = customer.id;
            dispatch(CustomerConditionActions.fetchCustomerConditions());
        }
    }, [conditionsState.conditions, customer?.id, dispatch]);

    // The tab reports itself valid until its form has been mounted, so the dot
    // beside it said nothing on a record nobody had opened the tab of.
    const conditionsIsValid = useMemo(
        () =>
            conditionsState.isValid &&
            0 ===
                Object.keys(paloSchemaErrors(customerDetailConditionsValidationSchema, conditionsState.conditions))
                    .length,
        [conditionsState.conditions, conditionsState.isValid]
    );

    const handleSave = async (): Promise<void> => {
        if (subPageRef.current) {
            await subPageRef.current.validate();
            await subPageRef.current.submit();
        }
        if (customer?.id) {
            dispatch(ActivityActions.fetchActivities(DocumentType.Customer, customer.id));
        }
        dispatch(CustomerDetailActions.saveCustomerDetails());
    };

    const handleDelete = (): void => {
        setShowDeleteWarning(false);
        dispatch(CustomerDetailActions.deleteCustomer());
    };

    const handleLockChanged = (): void => {
        setShowLockWarning(false);
        setShowUnlockWarning(false);
        dispatch(CustomerDetailActions.toggleLockCustomer());
    };

    const detailBase = ROUTES.contactsAndOrganizations.customers.detail.replace(
        ':id',
        String(customer?.id ?? customerIdParam)
    );
    const activeSegment = location.pathname.split('/').pop() ?? '';
    const activeTab = (Object.values(CustomerDetailTabs) as string[]).includes(activeSegment)
        ? (activeSegment as CustomerDetailTabs)
        : CustomerDetailTabs.Overview;

    // Reads the mark of the tab it labels, not "the mask has something unsaved
    // and this is the open tab" — the point of the marker is to say that
    // something is waiting on a tab nobody is looking at (#2159).
    const tabLabel = (tab: string, label: ReactElement, valid = true): ReactElement => (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            {label}
            {!valid && <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'error.main' }} />}
            <PaloDirtyDot scope={tab} />
        </Box>
    );

    // A-1: see the supplier — the state of a record is changed from the "⋯"
    // menu, never from a switch next to "Speichern".
    const actions = (
        <>
            {!isNew && (
                <PaloRecordMenu
                    label={intl.formatMessage({ id: 'app.common.moreActions' })}
                    actions={[
                        {
                            label: <FormattedMessage id={isLocked ? 'app.label.unlock' : 'app.label.lock'} />,
                            onClick: () => (isLocked ? setShowUnlockWarning(true) : setShowLockWarning(true)),
                        },
                        {
                            label: <FormattedMessage id="app.common.delete" />,
                            danger: true,
                            onClick: () => setShowDeleteWarning(true),
                        },
                    ]}
                />
            )}
            <PaloSaveAction
                phase={paloDocumentPhase(undefined, { saved: !isNew })}
                comparesToServer={true}
                onSave={handleSave}
            />
        </>
    );

    const tabSteps: PaloStep[] = [
        [CustomerDetailTabs.Overview, 'details', overviewIsValid, false],
        [CustomerDetailTabs.Addresses, 'addresses', true, false],
        [CustomerDetailTabs.Employee, 'employees', true, true],
        [CustomerDetailTabs.Documents, 'documents', true, true],
        [CustomerDetailTabs.Conditions, 'conditions', conditionsIsValid, true],
        [CustomerDetailTabs.Topics, 'topics', true, true],
        [CustomerDetailTabs.Transactions, 'transactions', true, true],
        [CustomerDetailTabs.ExternalCustomerMapping, 'externalCustomerMapping', true, true],
    ].map(([tab, key, valid, needsId]) => ({
        label: tabLabel(
            tab as string,
            <>
                <FormattedMessage id={`app.contactsAndOrganizations.detail.tab.${key}`} />
                {'addresses' === key && addressCount > 0 && (
                    <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400, ml: '5px' }}>
                        {addressCount}
                    </Box>
                )}
            </>,
            valid as boolean
        ),
        state: activeTab === tab ? 'now' : 'todo',
        disabled: (needsId as boolean) && !customer?.id,
        onClick: () => navigate(`${detailBase}/${tab}`),
    }));

    const tabs = <PaloTabs steps={tabSteps} />;

    return (
        <>
            <PoolDocumentTitle titleTranslationId={'app.contactsAndOrganizations.customer'} name={customerName} />
            <PaloDetailView
                themeContent={false}
                breadcrumbs={[
                    {
                        label: 'Mitglieder',
                        onClick: () => navigate(ROUTES.contactsAndOrganizations.customers.list),
                    },
                ]}
                title={customerName}
                status={
                    isLocked ? (
                        <Chip size="small" color="error" label={<FormattedMessage id="app.common.locked" />} />
                    ) : undefined
                }
                meta={
                    isMember && memberSince
                        ? [
                              {
                                  label: <FormattedMessage id="app.common.member" />,
                                  value: (
                                      <FormattedDate value={memberSince} day="2-digit" month="2-digit" year="numeric" />
                                  ),
                              },
                          ]
                        : undefined
                }
                actions={actions}
                alert={<PaloRecordNotices noteType={NoteType.Customers} entityId={customer?.id} />}
                tabs={tabs}
                dock={
                    <PaloRecordDock
                        count={documentCount}
                        attachments={customer?.id ? <PaloCustomerDocuments /> : undefined}
                    />
                }
                history={
                    <PaloHistory>
                        <DetailStream documentType={DocumentType.Customer} documentId={customer?.id} />
                    </PaloHistory>
                }
            >
                <Routes>
                    <Route
                        path={CustomerDetailTabs.Overview}
                        element={
                            <PaloDirtyScope name={CustomerDetailTabs.Overview}>
                                <CustomerDetailOverview ref={subPageRef} />
                            </PaloDirtyScope>
                        }
                    />
                    <Route
                        path={CustomerDetailTabs.Addresses}
                        element={
                            <PaloDirtyScope name={CustomerDetailTabs.Addresses}>
                                <CustomerDetailAddresses ref={subPageRef} />
                            </PaloDirtyScope>
                        }
                    />
                    <Route path={CustomerDetailTabs.Employee} element={<CustomerEmployees />} />
                    <Route path={CustomerDetailTabs.Documents} element={<CustomerDetailDocuments />} />
                    <Route
                        path={CustomerDetailTabs.Conditions}
                        element={
                            <PaloDirtyScope name={CustomerDetailTabs.Conditions}>
                                <CustomerDetailConditions ref={subPageRef} />
                            </PaloDirtyScope>
                        }
                    />
                    <Route path={CustomerDetailTabs.Topics} element={<CustomerTopics />} />
                    <Route
                        path={CustomerDetailTabs.Transactions}
                        element={
                            customer?.id ? <PaloAccountTransactions accountId={customer.id} side="customer" /> : null
                        }
                    />
                    <Route
                        path={CustomerDetailTabs.ExternalCustomerMapping}
                        element={
                            <PaloDirtyScope name={CustomerDetailTabs.ExternalCustomerMapping}>
                                <CustomerDetailExternalCustomerMapping ref={subPageRef} />
                            </PaloDirtyScope>
                        }
                    />
                    <Route
                        path="*"
                        element={<Navigate to={{ pathname: CustomerDetailTabs.Overview }} replace={true} />}
                    />
                </Routes>
            </PaloDetailView>

            <PaloConfirmationDialog
                isVisible={showDeleteWarning}
                title={<FormattedMessage id="app.contactsAndOrganizations.customer.delete" />}
                action={<FormattedMessage id="app.common.delete" />}
                onCancel={() => setShowDeleteWarning(false)}
                onConfirm={handleDelete}
            >
                <PoolFormattedMessage
                    id="app.contactsAndOrganizations.customer.deleteWarning"
                    values={{ name: customerName }}
                />
            </PaloConfirmationDialog>
            <PaloConfirmationDialog
                isVisible={showLockWarning}
                title={<FormattedMessage id="app.contactsAndOrganizations.customer.lockCustomer" />}
                action={<FormattedMessage id="app.label.lock" />}
                onCancel={() => setShowLockWarning(false)}
                onConfirm={handleLockChanged}
            >
                <PoolFormattedMessage
                    id="app.contactsAndOrganizations.customer.lockCustomer.text"
                    values={{ name: customerName }}
                />
            </PaloConfirmationDialog>
            <PaloConfirmationDialog
                isVisible={showUnlockWarning}
                title={<FormattedMessage id="app.contactsAndOrganizations.customer.unlockCustomer" />}
                action={<FormattedMessage id="app.label.unlock" />}
                onCancel={() => setShowUnlockWarning(false)}
                onConfirm={handleLockChanged}
            >
                <PoolFormattedMessage
                    id="app.contactsAndOrganizations.customer.unlockCustomer.text"
                    values={{ name: customerName }}
                />
            </PaloConfirmationDialog>
        </>
    );
};

export const PaloCustomerDetail = (): ReactElement => {
    const { id } = useParams<{ id: string }>();
    const recordKey = usePaloRecordKey(id);
    // A fresh document from the server is the one moment at which every part
    // of the mask, mounted or not, is provably saved.
    const loadedDocument = useAppSelector(
        state => state.contactsAndOrganizations.customers.customerDetail.overview.baseline
    );

    return (
        <PaloDirtyProvider
            key={recordKey}
            basePath={ROUTES.contactsAndOrganizations.customers.detail.replace(':id', String(id))}
            loadedDocument={loadedDocument}
        >
            <PaloCustomerDetailInner />
        </PaloDirtyProvider>
    );
};
